import { useState, useEffect } from 'react';
import { supervisorAPI } from '../../services/api';

const TYPE_LABELS = {
  pos: 'POS Tagging',
  features: 'Features',
  relations: 'Relaciones',
  ner: 'NER',
};

function ReannotationCard({ item, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await supervisorAPI.createReannotationBatch(item.annotation_type);
      setResult(res.data);
      // Only re-fetch pending list when manager was unavailable (no Prodigy session started).
      // When Prodigy started successfully the card switches to green; the yellow card will
      // disappear on its own once batch_monitor marks items as reannotated (polled every 15s).
      if (res.data?.manager_unavailable && onCreated) onCreated();
      if (res.data?.prodigy_url) {
        window.open(res.data.prodigy_url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al iniciar la re-anotación.');
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = TYPE_LABELS[item.annotation_type] || item.annotation_type.toUpperCase();

  // Estado final post-click
  if (result) {
    // Prodigy arrancó correctamente
    if (result.prodigy_url) {
      return (
        <div className="border border-green-300 bg-green-50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="font-semibold text-green-800">✅ {typeLabel} — listo para re-anotar</span>
            </div>
            <a
              href={result.prodigy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium transition-colors"
            >
              Abrir Prodigy ↗
            </a>
          </div>
          <p className="text-sm text-green-700">
            La sesión de re-anotación se abrió en una nueva pestaña.
            Si se cerró, hacé click en <strong>Abrir Prodigy ↗</strong>.
          </p>
        </div>
      );
    }

    // Manager no disponible → se notificó al supervisor
    if (result.manager_unavailable) {
      return (
        <div className="border border-orange-300 bg-orange-50 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xl">📨</span>
            <span className="font-semibold text-orange-800">{typeLabel} — solicitud enviada</span>
          </div>
          <p className="text-sm text-orange-700">
            Tu solicitud de re-anotación fue registrada y tu supervisor fue notificado
            para activar la sesión. Vas a recibir una notificación cuando esté lista.
          </p>
          <button
            onClick={handleStart}
            disabled={loading}
            className="mt-1 px-3 py-1.5 text-sm border border-orange-400 text-orange-700 rounded hover:bg-orange-100 transition-colors disabled:opacity-50"
          >
            {loading ? 'Verificando…' : 'Intentar de nuevo'}
          </button>
        </div>
      );
    }
  }

  return (
    <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="font-semibold text-yellow-800">↩ {typeLabel}</span>
          <span className="ml-2 text-sm text-yellow-700">
            {item.count} {item.count === 1 ? 'ítem devuelto' : 'ítems devueltos'}
          </span>
        </div>
        <button
          onClick={handleStart}
          disabled={loading}
          className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded font-medium disabled:opacity-50 transition-colors flex items-center gap-1.5"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Iniciando…
            </>
          ) : '▶ Re-anotar ahora'}
        </button>
      </div>
      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  );
}

export default function ReannotationPanel() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    supervisorAPI.getReannotationPending()
      .then(res => setPending(res.data))
      .catch(() => setPending([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // Poll every 15s so that when batch_monitor marks items as reannotated
    // the panel disappears automatically without a manual page reload
    const interval = setInterval(refresh, 15000);
    return () => clearInterval(interval);
  }, []);

  if (loading || pending.length === 0) return null;

  return (
    <div className="mb-6 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-yellow-800">↩ Anotaciones devueltas para revisar</h2>
        <p className="text-sm text-yellow-700 mt-0.5">
          Tu supervisor revisó estas anotaciones y las devolvió. Hacé click en <strong>Re-anotar ahora</strong> para corregirlas.
        </p>
      </div>
      <div className="space-y-3">
        {pending.map(item => (
          <ReannotationCard key={item.annotation_type} item={item} onCreated={refresh} />
        ))}
      </div>
    </div>
  );
}

